import { useAccount, useMsal } from '@azure/msal-react'
import { EFinancingCompany } from '@if/core/types'
import { useMenu } from '@if/mui'
import { Button, Menu, MenuItem } from '@mui/material'
import { postMockLogin } from '@src/api/user-api'
import { JWT_LOCAL_STORAGE_KEY } from '@src/contexts/user-context'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { apiClient } from '@src/services/http-common'
import { CLEAR_CACHE_EXCEPTION_KEY, useDevTools } from '@src/types/constants'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import MockUserPicker from './Components/MockUserPicker'

const clearCache = () => {
  const persistentValues: Record<string, string> = {}
  CLEAR_CACHE_EXCEPTION_KEY.forEach((key) => {
    const cachedValue = localStorage.getItem(key)
    if (cachedValue) persistentValues[key] = cachedValue
  }, {})
  localStorage.clear()
  Object.entries(persistentValues).forEach(([key, value]) => {
    localStorage.setItem(key, value)
  })
}

const ProfileMenu = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { instance } = useMsal()
  const account = useAccount()
  const [isMockUserDialogOpen, setIsMockUserDialogOpen] = useState<boolean>(false)
  const [buttonProps, menuProps, handleMenuClose] = useMenu()
  const user = useUser()

  const mockLogin = (username: string, companyIds: EFinancingCompany[]) => {
    postMockLogin({ username, password: 'ok', companyIds })
      .then(({ data }) => {
        localStorage.setItem(JWT_LOCAL_STORAGE_KEY, data.jwt)
        apiClient.defaults.headers.common.Authorization = `Bearer ${data.jwt}`
        setIsMockUserDialogOpen(false)
        window.location.reload()
      })
      .catch(reportErrorToConsole)
  }

  const handleMockUserDialogOpen = () => {
    handleMenuClose()
    setIsMockUserDialogOpen(true)
  }

  const handleLogout = async () => {
    handleMenuClose()
    clearCache()
    if (account) await instance.logoutRedirect({ account })
    else void navigate('/')
  }

  return (
    <>
      <Button {...buttonProps}>{user?.name}</Button>
      <Menu {...menuProps}>
        {useDevTools && <MenuItem onClick={handleMockUserDialogOpen}>{t('login.mockLogin')}</MenuItem>}
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          {t('menus.logout')}
        </MenuItem>
      </Menu>
      <MockUserPicker
        open={isMockUserDialogOpen}
        onMockedLoginSelected={mockLogin}
        onClose={() => setIsMockUserDialogOpen(false)}
      />
    </>
  )
}

export default ProfileMenu
